import React from "react";
import { AccountIcon, Co2Icon, DataIOIcon, InsightsIcon, KnowHowIcon, MasterDataIcon, SettingsIcon, WhatIfIcon } from "../assets/icons";
import { AppMenuKey, AppRoutes } from "../common/routesUtils";
import { FilterContexType } from "../components/layout/ViewHeader";
import { PageType } from "./page.types";

// Breadcrumb types
export type StaticBreadcrumb = {
	icon?: React.ElementType;
	label: AppMenuKey;
	path?: AppRoutes;
	isLabelOnly?: boolean; // used just to show labels without link
};

export type StaticPageLayout = {
	filterContext?: FilterContexType;
	breadcrumbs?: StaticBreadcrumb[];
	title?: string;
	subtitle?: string;
};

// Helper: create one or multiple breadcrumbs
const createBreadcrumbs = (
	...crumbs: Array<{ icon?: React.ElementType; label: AppMenuKey; path?: AppRoutes; isLabelOnly?: boolean }>
): StaticBreadcrumb[] => crumbs;

// Page layout configuration
export const PAGE_LAYOUT: Record<PageType, StaticPageLayout> = {
	// Master data & data configuration
	[PageType.MasterDataOperationalUnits]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: MasterDataIcon, label: AppMenuKey.MasterData, isLabelOnly: true },
			{
				label: AppMenuKey.MasterDataOperationalUnits,
				path: AppRoutes.MasterDataOperationalUnits,
			}
		),
		title: PageType.MasterDataOperationalUnits,
	},

	[PageType.MasterDataOperationalUnitsAddUnit]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: MasterDataIcon, label: AppMenuKey.MasterData, isLabelOnly: true },
			{
				label: AppMenuKey.MasterDataOperationalUnits,
				path: AppRoutes.MasterDataOperationalUnits,
			},
			{
				label: AppMenuKey.MasterDataOperationalUnitsAddUnit,
				path: AppRoutes.MasterDataOperationalUnitsAddUnit,
			}
		),
		title: PageType.MasterDataOperationalUnitsAddUnit,
	},

	[PageType.MasterDataOperationalUnitsDetail]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: MasterDataIcon, label: AppMenuKey.MasterData, isLabelOnly: true },
			{
				label: AppMenuKey.MasterDataOperationalUnits,
				path: AppRoutes.MasterDataOperationalUnits,
			},
			{
				label: AppMenuKey.MasterDataOperationalUnitsDetail,
				path: AppRoutes.MasterDataOperationalUnitsDetail,
			}
		),
		title: PageType.MasterDataOperationalUnitsDetail,
	},
	[PageType.MasterDataAssumptions]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: MasterDataIcon, label: AppMenuKey.MasterData, isLabelOnly: true },
			{ label: AppMenuKey.MasterDataAssumptions, path: AppRoutes.MasterDataAssumptions }
		),
	},
	[PageType.MasterDataFleetsVehicles]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: MasterDataIcon, label: AppMenuKey.MasterData, isLabelOnly: true },
			{ label: AppMenuKey.MasterDataFleetsVehicles, path: AppRoutes.MasterDataFleetsVehicles }
		),
	},
	[PageType.MasterDataNetworksRoutes]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: MasterDataIcon, label: AppMenuKey.MasterData, isLabelOnly: true },
			{ label: AppMenuKey.MasterDataNetworksRoutes, path: AppRoutes.MasterDataNetworksRoutes }
		),
	},
	[PageType.MasterDataConversionTables]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: MasterDataIcon, label: AppMenuKey.MasterData, isLabelOnly: true },
			{ label: AppMenuKey.MasterDataConversionTables, path: AppRoutes.MasterDataConversionTables }
		),
	},

	// Results
	[PageType.ResultsOverview]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOverview, path: AppRoutes.ResultsOverview }
		),
	},

	// Data Insights
	[PageType.DataInsightsDashboard]: {
		filterContext: ["trips"],
		breadcrumbs: createBreadcrumbs(
			{ icon: InsightsIcon, label: AppMenuKey.DataInsights, isLabelOnly: true },
			{ label: AppMenuKey.DataInsightsDashboardInsights, path: AppRoutes.DataInsightsDashboardInsights }
		),
	},
	[PageType.DataInsightsDashboardDetail]: {
		filterContext: ["trips"],
		breadcrumbs: createBreadcrumbs(
			{ icon: InsightsIcon, label: AppMenuKey.DataInsights, isLabelOnly: true },
			{ label: AppMenuKey.DataInsightsDashboardInsights, path: AppRoutes.DataInsightsDashboardInsights },
			{ label: AppMenuKey.DataInsightsDashboardInsightsDetail, path: AppRoutes.DataInsightsDashboardInsightsDetail }
		),
	},
	[PageType.DataInsightsHotspots]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: InsightsIcon, label: AppMenuKey.DataInsights, isLabelOnly: true },
			{ label: AppMenuKey.DataInsightsHotspots, path: AppRoutes.DataInsightsHotspots }
		),
	},
	[PageType.DataInsightsRootCause]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: InsightsIcon, label: AppMenuKey.DataInsights, isLabelOnly: true },
			{ label: AppMenuKey.DataInsightsRootCause, path: AppRoutes.DataInsightsRootCause }
		),
		title: PageType.DataInsightsRootCause,
		subtitle: PageType.DataInsightsRootCause,
	},
	[PageType.DataInsightsRootCauseResults]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: InsightsIcon, label: AppMenuKey.DataInsights, isLabelOnly: true },
			{ label: AppMenuKey.DataInsightsRootCause, path: AppRoutes.DataInsightsRootCause },
			{ label: AppMenuKey.DataInsightsRootCauseResults, path: AppRoutes.DataInsightsRootCauseResults }
		),
		title: PageType.DataInsightsRootCauseResults,
		subtitle: PageType.DataInsightsRootCauseResults,
	},

	// Transport
	[PageType.ResultsTransport]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport }
		),
	},
	[PageType.ResultsTransportGreenRouter]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportGreenrouter, path: AppRoutes.ResultsTransportGreenrouter }
		),
	},
	[PageType.ResultsTransportGreenRouterEntities]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportGreenrouter, path: AppRoutes.ResultsTransportGreenrouter }
		),
	},
	[PageType.ResultsTransportGreenRouterDetail]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportGreenrouter, path: AppRoutes.ResultsTransportGreenrouter },
			{ label: AppMenuKey.ResultsTransportGreenrouterDetail, path: AppRoutes.ResultsTransportGreenrouterDetail }
		),
	},
	[PageType.ResultsTransportGreenRouterEntityDetail]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportGreenrouter, path: AppRoutes.ResultsTransportGreenrouter },
			{
				label: AppMenuKey.ResultsTransportGreenrouterDetail,
				path: AppRoutes.ResultsTransportGreenrouterEntityDetail,
			}
		),
	},

	[PageType.ResultsTransportThirdParty]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportThirdParty, path: AppRoutes.ResultsTransportThirdParty }
		),
	},
	[PageType.ResultsTransportThirdPartyDetail]: {
		filterContext: ["trips", "nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsTransport, path: AppRoutes.ResultsTransport },
			{ label: AppMenuKey.ResultsTransportThirdParty, path: AppRoutes.ResultsTransportThirdParty },
			{ label: AppMenuKey.ResultsTransportThirdPartyDetail, path: AppRoutes.ResultsTransportThirdPartyDetail }
		),
	},

	// Operational Units
	[PageType.ResultsOperationalUnits]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits }
		),
	},
	[PageType.ResultsOperationalUnitsGreenRouter]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits },
			{ label: AppMenuKey.ResultsOperationalUnitsGreenRouter, path: AppRoutes.ResultsOperationalUnitsGreenRouter }
		),
	},
	[PageType.ResultsOperationalUnitsGreenRouterDetail]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits },
			{
				label: AppMenuKey.ResultsOperationalUnitsGreenRouter,
				path: AppRoutes.ResultsOperationalUnitsGreenRouter,
			},
			{
				label: AppMenuKey.ResultOperationalUnitsGreerouterNode,
				path: AppRoutes.ResultOperationalUnitsGreerouterNode,
			}
		),
	},
	[PageType.ResultsOperationalUnitsThirdParty]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits },
			{ label: AppMenuKey.ResultsOperationalUnitsThirdParty, path: AppRoutes.ResultsOperationalUnitsThirdParty }
		),
	},
	[PageType.ResultsOperationalUnitsThirdPartyDetail]: {
		filterContext: ["nodes"],
		breadcrumbs: createBreadcrumbs(
			{ icon: Co2Icon, label: AppMenuKey.Results, isLabelOnly: true },
			{ label: AppMenuKey.ResultsOperationalUnits, path: AppRoutes.ResultsOperationalUnits },
			{ label: AppMenuKey.ResultsOperationalUnitsThirdParty, path: AppRoutes.ResultsOperationalUnitsThirdParty },
			{
				label: AppMenuKey.ResultsOperationalUnitsThirdPartyDetail,
				path: AppRoutes.ResultsOperationalUnitsThirdPartyDetail,
			}
		),
	},

	// What If
	[PageType.WhatIfBudgeting]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: WhatIfIcon, label: AppMenuKey.WhatIf, isLabelOnly: true },
			{ label: AppMenuKey.WhatIfBudgeting, path: AppRoutes.WhatIfBudgeting }
		),
	},
	[PageType.WhatIfSimulations]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: WhatIfIcon, label: AppMenuKey.WhatIf, isLabelOnly: true },
			{ label: AppMenuKey.WhatIfSimulations, path: AppRoutes.WhatIfSimulations }
		),
	},
	[PageType.WhatIfCarbonSnap]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: WhatIfIcon, label: AppMenuKey.WhatIf, isLabelOnly: true },
			{ label: AppMenuKey.WhatIfCarbonSnap, path: AppRoutes.WhatIfCarbonSnap }
		),
		title: PageType.WhatIfCarbonSnap,
	},
	[PageType.WhatIfCarbonSnapResults]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: WhatIfIcon, label: AppMenuKey.WhatIf, isLabelOnly: true },
			{ label: AppMenuKey.WhatIfCarbonSnap, path: AppRoutes.WhatIfCarbonSnap },
			{
				label: AppMenuKey.WhatIfCarbonSnapResults,
				path: AppRoutes.WhatIfCarbonSnapResults,
			}
		),
	},

	// Data I/O
	[PageType.DataIOMonitor]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIOIcon, label: AppMenuKey.DataIO, isLabelOnly: true },
			{ label: AppMenuKey.DataIOMonitor, path: AppRoutes.DataIOMonitor }
		),
	},
	[PageType.DataIOImport]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIOIcon, label: AppMenuKey.DataIO, isLabelOnly: true },
			{ label: AppMenuKey.DataIOImport, path: AppRoutes.DataIOImport }
		),
	},
	[PageType.DataIOAutomatedProcesses]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIOIcon, label: AppMenuKey.DataIO, isLabelOnly: true },
			{ label: AppMenuKey.DataIOAutomatedProcesses, path: AppRoutes.DataIOAutomatedProcesses }
		),
	},
	[PageType.DataIODataExport]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: DataIOIcon, label: AppMenuKey.DataIO, isLabelOnly: true },
			{ label: AppMenuKey.DataIODataExport, path: AppRoutes.DataIODataExport }
		),
	},

	// Know how
	[PageType.KnowHowUserManuals]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: KnowHowIcon, label: AppMenuKey.KnowHow, isLabelOnly: true },
			{ label: AppMenuKey.KnowHowUserManuals, path: AppRoutes.KnowHowUserManuals }
		),
	},
	[PageType.KnowHowKnowledgeBase]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: KnowHowIcon, label: AppMenuKey.KnowHow, isLabelOnly: true },
			{ label: AppMenuKey.KnowHowKnowledgeBase, path: AppRoutes.KnowHowKnowledgeBase }
		),
	},
	[PageType.KnowHowApiDocumentation]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: KnowHowIcon, label: AppMenuKey.KnowHow, isLabelOnly: true },
			{ label: AppMenuKey.KnowHowApiDocumentation, path: AppRoutes.KnowHowApiDocumentation }
		),
	},

	// Platform settings
	[PageType.PlatformSettingsDataDomains]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: SettingsIcon, label: AppMenuKey.PlatformSettings, isLabelOnly: true },
			{ label: AppMenuKey.PlatformSettingsDataDomains, path: AppRoutes.PlatformSettingsDataDomains }
		),
	},
	[PageType.PlatformSettingsAdmin]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: SettingsIcon, label: AppMenuKey.PlatformSettings, isLabelOnly: true },
			{ label: AppMenuKey.PlatformSettingsAdmin, path: AppRoutes.PlatformSettingsAdmin }
		),
	},
	[PageType.PlatformSettingsApi]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: SettingsIcon, label: AppMenuKey.PlatformSettings, isLabelOnly: true },
			{ label: AppMenuKey.PlatformSettingsApi, path: AppRoutes.PlatformSettingsApi }
		),
	},

	// Account
	[PageType.Account]: {
		breadcrumbs: createBreadcrumbs(
			{ icon: AccountIcon, label: AppMenuKey.Account, isLabelOnly: true },
			{ label: AppMenuKey.Account, path: AppRoutes.Account }
		),
	},
};
